/**
 * Renders a game's wordmark with the treatment from its physical box.
 *
 * I'd Eat That — gold-foil title, debossed-into-foil look. A single
 *   warm gold with a subtle darker edge suggests the shine of the
 *   real package (true metallic foil only fakes out on print).
 *
 * Colorful — each letter a different color, cycling through the
 *   rainbow like the debossed title on the cream box. Mono-spaced
 *   feel to match the box typography.
 *
 * For other games, falls back to a plain bold wordmark using the
 * per-game accent color token from the palette. Extend when more
 * games ship.
 */

export type GameId =
  | 'ideatthat'
  | 'colorful'
  | 'three-step'
  | 'tokyo-metro'
  | 'tokyo-jutaku'

interface Props {
  game: GameId
  /** Preset size. Also controls letter spacing so the wordmark reads clean at each scale. */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

// Colorful letter palette — red → orange → yellow → green → teal → blue → purple, looped.
// Values sampled to match the cream-box cover art.
const COLORFUL_LETTER_COLORS = [
  '#B8353C', // C — crimson
  '#D4713A', // O — warm orange
  '#C9A93A', // L — ochre
  '#C9A93A', // O — ochre
  '#7A4B3A', // R — russet
  '#2F5F6A', // F — petrol blue
  '#5A3A62', // U — aubergine
  '#B8353C', // L — crimson (loop)
]

const SIZE_CLASSES: Record<NonNullable<Props['size']>, string> = {
  sm: 'text-lg tracking-[0.06em]',
  md: 'text-2xl tracking-[0.08em]',
  lg: 'text-4xl tracking-widest',
  xl: 'text-6xl tracking-[0.12em]',
}

export function GameTitle({ game, size = 'md', className = '' }: Props) {
  const sizing = SIZE_CLASSES[size]

  if (game === 'ideatthat') {
    // Gold-foil look: warm gold base, subtle inner shadow to feel debossed.
    return (
      <span
        className={`font-display font-black uppercase ${sizing} ${className}`}
        style={{
          background: 'linear-gradient(180deg, #E6B94A 0%, #C89420 55%, #A67A18 100%)',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          textShadow: '0 1px 0 rgba(255,255,255,0.25), 0 -1px 0 rgba(0,0,0,0.12)',
        }}
      >
        I&rsquo;D EAT THAT
      </span>
    )
  }

  if (game === 'colorful') {
    // Per-letter rainbow but rendered as flush inline text (no
    // inline-flex / no extra letter-spacing) so the wordmark matches
    // the tightness of the other game titles.
    const letters = 'COLORFUL'.split('')
    return (
      <span className={`font-display font-black uppercase ${sizing} ${className}`}>
        {letters.map((ch, i) => (
          <span key={i} style={{ color: COLORFUL_LETTER_COLORS[i % COLORFUL_LETTER_COLORS.length] }}>
            {ch}
          </span>
        ))}
      </span>
    )
  }

  if (game === 'three-step') {
    // Stroke-color variant of the brand mint (#0EAE93) — the user-
    // supplied #1FFFD9 is the canonical brand hue, but it reads as
    // an unreadably bright wash on the light-mint card background.
    // We use the deeper "dark" sibling here so the wordmark stays
    // legible against `bg-game-three-step-light`.
    return (
      <span
        className={`font-display font-black uppercase ${sizing} ${className}`}
        style={{ color: '#0EAE93' }}
      >
        THREE STEP SOLUTION
      </span>
    )
  }

  if (game === 'tokyo-metro') {
    // Brand orange — same shade Jordan Draper uses across the V2
    // rulebook (section headers, contents callouts, page numbers)
    // and the duotoned cover-photo collage. Matches the canonical
    // Ginza-line color in the real Tokyo Metro system.
    return (
      <span
        className={`font-display font-black uppercase ${sizing} ${className}`}
        style={{ color: '#F59B1F' }}
      >
        TOKYO METRO
      </span>
    )
  }

  if (game === 'tokyo-jutaku') {
    return (
      <span
        className={`font-display font-black uppercase ${sizing} ${className}`}
        style={{ color: '#111111' }}
      >
        TOKYO JUTAKU
      </span>
    )
  }

  return null
}
